import React from "react";
import styles from "./ota-update-summary.module.scss";
import cn from "classnames";
import UserIcon from "@src/assets/svg/user-white.svg";
import {
  OTA_UPDATE_SUMMARY as cssClassName,
  FIRMWARE_TABS,
} from "../../../cloud-connect/ota-update/constants";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { RenderIfUserHasPermission } from "../render-if-user-has-permission/render-if-user-has-permission";
import {
  ActiveInstallationProcessPayload,
  ListItem,
} from "@src/types/ota-update";
import { RenderIfUserHasMultiplePermissions } from "../render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { useOTAUpdateSummary } from "./use-ota-update-summary";

// Props definition for OTA update summary component
interface OTAUpdateSummaryProps {
  cardData: ListItem | null;
  onInstallStarted?: (updateId: string) => void;
  activeOTAHistoryTab: string;
  onSelectPushFirmwareRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  onSelectPushFirmwareBackupRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  activeInstallationData: ActiveInstallationProcessPayload | null;
}

// Renders OTA update metadata, uploader info, and action buttons
const OTAUpdateSummary = ({
  cardData,
  onInstallStarted,
  activeOTAHistoryTab,
  onSelectPushFirmwareRow,
  onSelectPushFirmwareBackupRow,
  activeInstallationData,
}: OTAUpdateSummaryProps) => {
  const {
    texts,
    title,
    infoText,
    releaseFields,
    downloadPermission,
    isFirmwareAlreadyInstalled,
    isInstallationGoingOn,
    installActionTooltip,
    onDownload,
    handlePushFirmware,
    handlePushFirmwareBackup,
    shouldShowFirmwareButton,
  } = useOTAUpdateSummary({
    cardData,
    onInstallStarted,
    activeOTAHistoryTab,
    onSelectPushFirmwareRow,
    onSelectPushFirmwareBackupRow,
    activeInstallationData,
  });

  return (
    // Root container for OTA update summary card
    <div className={styles[cssClassName]}>
      {/* Displays latest badge and version information */}
      <FlexRowContainer>
        <p className={styles[`${cssClassName}__badge`]}>{texts.latestText}</p>
        {cardData?.version && (
          <p className={styles[`${cssClassName}__version`]}>
            {texts.versionText} {cardData?.version}
          </p>
        )}
      </FlexRowContainer>

      {/* Displays firmware filename or empty state title */}
      <h3
        className={cn(styles[`${cssClassName}__title`], {
          [styles[`${cssClassName}__title--with-no-data`]]: !cardData,
        })}
      >
        {title}
      </h3>

      {/* Displays info message when no data is available */}
      {!cardData && (
        <p className={styles[`${cssClassName}__info`]}>{infoText}</p>
      )}

      {/* Displays size, digest, and upload time metadata */}
      <Spacer16>
        <div className={styles[`${cssClassName}__meta-row`]}>
          {releaseFields?.map((field) => {
            return (
              <div
                key={field.label}
                className={styles[`${cssClassName}__meta-item`]}
              >
                <span className={styles[`${cssClassName}__meta-label`]}>
                  {field.label}
                </span>
                <span className={styles[`${cssClassName}__meta-value`]}>
                  {field.isDate && field.value ? (
                    <TimezoneDate date={field.value} />
                  ) : (
                    field.value
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </Spacer16>

      {/* Displays uploader information */}
      {cardData && (
        <Spacer16>
          <FlexRowContainer>
            <img src={UserIcon} alt={texts.uploaderAltText} />
            <p className={styles[`${cssClassName}__firm-label`]}>
              {cardData?.uploader?.display_name}
            </p>
          </FlexRowContainer>
        </Spacer16>
      )}

      {/* Displays install/download actions and pre-install check indicator */}
      {cardData && (
        <FlexRowContainer>
          {shouldShowFirmwareButton && (
            <RenderIfUserHasPermission permission={"initiate-update-process"}>
              <Button
                fun={
                  activeOTAHistoryTab === FIRMWARE_TABS.BACKUPS
                    ? handlePushFirmwareBackup
                    : handlePushFirmware
                }
                text={
                  activeOTAHistoryTab === FIRMWARE_TABS.BACKUPS
                    ? texts.pushBackupText
                    : texts.pushFirmwareText
                }
                style={{
                  flexShrink: 0,
                }}
                disabled={isFirmwareAlreadyInstalled || isInstallationGoingOn}
                variant="primary"
                tooltip={installActionTooltip}
              />
            </RenderIfUserHasPermission>
          )}
          <RenderIfUserHasMultiplePermissions
            permissions={["initiate-update-process", downloadPermission]}
          >
            <Button
              fun={() => onDownload(cardData?.firmware_id, cardData?.type)}
              text={texts.downloadText}
              variant="primary"
              style={{
                flexShrink: 0,
              }}
            />
          </RenderIfUserHasMultiplePermissions>
        </FlexRowContainer>
      )}
    </div>
  );
};

export default OTAUpdateSummary;
